import { LOGO_H } from './logoH';

/**
 * MYKLUBB — Plantilla del rol de jornada.
 * Sin dependencias. Devuelve un string de HTML listo para renderizar.
 *
 * Ver SPEC.md para el contrato de datos, las reglas y las constantes medidas.
 */

// ---------- Constantes medidas sobre render real. No cambiar sin volver a medir. ----------
export const METRICAS = {
  // Cabecera de una linea: el escudo a la izquierda y los titulos a su derecha, a la
  // misma altura (el dueno, 2026-09-09: "la imagen y el texto ocupan mucho espacio").
  // Antes eran 254 px con el logo encima del texto. El bloque tiene altura fija por CSS,
  // asi que esta medida y el render coinciden por construccion.
  CABECERA: 186,
  PIE: 141,
  ZONAS_ANOTACION: 106,
  FILA: 129,          // 117 de tarjeta + 12 de separación
  COLUMNA_MIN: 226,   // ancho irreductible por campo
  TOPE_WHATSAPP: 1600 // lado más largo
};

const LIENZOS = [
  { maxCampos: 4, maxHorarios: 6,  w: 1080, h: 1350, orientacion: 'vertical' },
  { maxCampos: 4, maxHorarios: 8,  w: 1080, h: 1560, orientacion: 'vertical' },
  { maxCampos: 4, maxHorarios: 11, w: 1080, h: 1920, orientacion: 'vertical' },
  { maxCampos: 6, maxHorarios: 10, w: 1600, h: 1350, orientacion: 'horizontal' }
];

/** Elige lienzo o indica que hay que partir en varias imágenes. */
export function elegirLienzo(nCampos, nHorarios) {
  const l = LIENZOS.find(x => nCampos <= x.maxCampos && nHorarios <= x.maxHorarios);
  if (l) return { ...l, partir: null };
  if (nCampos <= 12) {
    const mitad = Math.ceil(nCampos / 2);
    return { w: 1600, h: 1350, orientacion: 'horizontal',
             partir: { por: 'campos', tamano: Math.min(mitad, 6) } };
  }
  return { pdf: true, motivo: 'Más de 12 canchas: usa PDF, no imagen.' };
}

/** Valida antes de renderizar. Devuelve [] si todo está bien. */
export function validarRol(rol, clubesPorId) {
  const e = [];
  const usados = new Set();
  rol.partidos.forEach(p => { usados.add(p.local_id); usados.add(p.visitante_id); });

  usados.forEach(id => {
    const c = clubesPorId[id];
    if (!c) return e.push(`Club desconocido: ${id}`);
    if (!c.nombre && !c.nombre_corto) return e.push(`Club ${id}: sin nombre`);
    const n = nombreCorto(c);
    if (tamanoNombre(n) < 19)
      e.push(`"${c.nombre}" se muestra como "${n}" y no cabe con buen tamaño. ` +
             `Define nombre_corto para este club o acorta el nombre.`);
  });

  rol.categorias.forEach(c => {
    if (c.etiqueta_corta.length > 11)
      e.push(`Categoría "${c.etiqueta_corta}" excede 11 caracteres`);
  });

  const horarios = new Set(rol.horarios);
  rol.partidos.forEach(p => {
    if (!horarios.has(p.horario))
      e.push(`Partido ${p.id}: horario ${p.horario} no está en el riel. ` +
             `El riel exige bloques alineados entre canchas.`);
  });

  // 1) Dos partidos no caben en la misma celda de la rejilla: la imagen perderia uno sin
  //    avisar. Y en la cancha tampoco caben: mismo campo y misma hora es imposible.
  const nombreDeCampo = Object.fromEntries(rol.campos.map(c => [c.id, c.nombre]));
  const porCelda = new Map();
  rol.partidos.forEach(p => {
    const k = `${p.campo_id}|${p.horario}`;
    porCelda.set(k, (porCelda.get(k) || 0) + 1);
  });
  porCelda.forEach((n, k) => {
    if (n < 2) return;
    const [campoId, horario] = k.split('|');
    const nombre = nombreDeCampo[campoId] ?? campoId;
    e.push(nombre === 'Sin cancha'
      ? `Hay ${n} partidos a las ${horario} sin cancha asignada: captura la cancha de cada uno para que todos salgan en el calendario.`
      : `${nombre} tiene ${n} partidos a las ${horario}`);
  });

  // 2) Un equipo no juega dos partidos a la vez. `varios_equipos` marca a las entidades
  //    que NO son un equipo sino un club entero: el rival de un torneo interno se llama
  //    como el club ("DINOS"), y ese club si puede traer dos equipos a la misma hora.
  //    En la red cada inscripcion es club + grupo, asi que todas son equipo.
  const ocupacion = new Map();
  rol.partidos.forEach(p => {
    [p.local_id, p.visitante_id].forEach(cid => {
      const c = clubesPorId[cid];
      if (c?.varios_equipos) return;
      const k = `${cid}@${p.horario}`;
      if (ocupacion.has(k))
        e.push(`${c?.etiqueta ?? c?.nombre ?? cid} tiene dos partidos a las ${p.horario}`);
      ocupacion.set(k, p.id);
    });
  });

  const l = elegirLienzo(rol.campos.length, filasDelRol(rol));
  if (l.pdf) e.push(l.motivo);
  // El mismo club puede venir varias veces (un equipo por grupo): su aviso de nombre
  // largo se dice una vez, no uno por grupo.
  return Array.from(new Set(e));
}

// ---------- Temas ----------
const TEMAS = {
  oscuro: {
    fondo: '#0D0D0D', tinta: '#FFFFFF', tenue: '#8A8A8A', apagado: '#5E5E5E',
    linea: '#2C2C2C', tarjeta: 'rgba(9,9,9,.9)', zona: '#191919',
    cancha: '#121212', vetas: 'rgba(255,255,255,.05)', hash: 'rgba(255,255,255,.10)',
    diagonal: '#B3F000', filete: '#B3F000', zonaTexto: '#FFFFFF',
    visitante: '#ABABAB', sombra: 'none', colorCat: 'color_oscuro'
  },
  claro: {
    fondo: '#F2F2EF', tinta: '#0D0D0D', tenue: '#565656', apagado: '#7C7C7C',
    linea: 'rgba(13,13,13,.18)', tarjeta: '#FFFFFF', zona: '#0D0D0D',
    cancha: 'rgba(255,255,255,.22)', vetas: 'rgba(13,13,13,.07)', hash: 'rgba(13,13,13,.15)',
    diagonal: '#0D0D0D', filete: '#B3F000', zonaTexto: '#FFFFFF',
    visitante: '#575757', sombra: '0 1px 3px rgba(13,13,13,.14)', colorCat: 'color_claro'
  }
};

const esc = s => String(s).replace(/[&<>"]/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// ---------- Nombre a mostrar ----------
// No requiere columnas nuevas. Usa nombre_corto si el club ya lo tiene; si no, lo deriva.
const PREFIJOS = /^(club\s+deportivo|club|deportivo|academia|equipo|c\.?d\.?|a\.?d\.?)\s+/i;

export function nombreCorto(club) {
  if (club.nombre_corto) return club.nombre_corto;
  const base = String(club.nombre).replace(PREFIJOS, '').trim();
  const primera = base.split(/\s+/)[0];
  return primera.length >= 4 ? primera : base;
}

// Ancho útil para el nombre dentro de la tarjeta, en px (medido: 190).
const ANCHO_NOMBRE = 190;
const AVANCE = 0.51;   // ancho medio de carácter en Barlow Condensed 600, en em

/** Ajusta el cuerpo para que el nombre quepa sin recortarse. */
export function tamanoNombre(texto) {
  const ideal = Math.floor(ANCHO_NOMBRE / (AVANCE * Math.max(texto.length, 1)));
  return Math.max(17, Math.min(23, ideal));
}

// eslint-disable-next-line no-unused-vars
const MK = `<svg viewBox="0 0 62 44"><path class="mkw" fill="#FFF" d="M0 44V10.5l8.5 8.6V44H0zM0 0l17.5 17.7L35 0v11.6L17.5 29.3 0 11.6V0z"/><path class="mkw" fill="#FFF" d="M27 44V32.4L33 26v18h-6z"/><path fill="#B3F000" d="M27 26.4L53.5 0H62L27 36V26.4z"/><path class="mkw" fill="#FFF" d="M38 25.5L56.5 44H46L33 30.6l5-5.1z"/></svg>`;

function iniciales(club) {
  if (club.iniciales) return club.iniciales;
  return String(club.nombre).replace(PREFIJOS, '').split(/\s+/)
    .slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase();
}

function escudo(club, px) {
  if (club.escudo_url)
    return `<img class="lg" src="${esc(club.escudo_url)}" alt="" width="${px}" height="${px}">`;
  return `<svg class="lg" width="${px}" height="${px}" viewBox="0 0 40 40">
    <path d="M20 2 L36 7 V21 C36 30 28 36 20 38 C12 36 4 30 4 21 V7 Z" fill="#3C6E9E" stroke="#EAEAEA" stroke-width="1.6"/>
    <text x="20" y="26" font-family="Barlow Condensed" font-weight="800" font-size="15"
      fill="#FFF" text-anchor="middle">${esc(iniciales(club))}</text></svg>`;
}

// ---------- Adaptaciones MYKLUBB (2026-09-07, acordadas con el dueno) ----------
// - Sin hora de fin: el riel solo lleva la hora de inicio.
// - Sin version: se estampa la fecha y hora de generacion (rol.generado_en).
// - Tolerancia y coordinador solo si existen (en la base no existen).
// - Una fila por horario, siempre (el dueno, 2026-09-08): dos horas distintas nunca
//   comparten renglon. Si no caben, se parte en varias imagenes; nunca se apilan.
// - `renderRolFragmento` devuelve { w, h, css, html } con el CSS acotado a `.rolj` para
//   pintarse dentro de la app (el modal Compartir rol); `renderRol` sigue devolviendo el
//   documento completo.

/** Filas del rol: una por horario. Es la promesa del formato (SPEC §1). */
export function filasDelRol(rol) {
  return rol.horarios.length;
}

/** Cuantas filas caben con ese numero de campos, segun la tabla de lienzos (SPEC §3). */
export function maxFilas(nCampos) {
  const cabe = LIENZOS.filter(l => nCampos <= l.maxCampos).map(l => l.maxHorarios);
  return cabe.length ? Math.max(...cabe) : 0;
}

/**
 * Parte el rol en varias imagenes cuando no cabe en un lienzo (SPEC §3): primero por
 * campos (7-12), y despues por horarios, porque una fila es un horario y no se encogen
 * ni se apilan. Devuelve la lista de bloques, cada uno con su `parte` y `partes`.
 */
export function partirRol(rol) {
  const nc = rol.campos.length;
  const L = elegirLienzo(nc, filasDelRol(rol));
  if (L.pdf) throw new Error(L.motivo);

  // 1) por campos, solo si el problema es el ANCHO. Con pocos campos y muchos horarios
  //    lo que sobra son filas, y eso se resuelve abajo.
  let porCampos = [rol];
  if (maxFilas(nc) === 0) {
    const n = Math.min(Math.ceil(nc / 2), 6);
    porCampos = [];
    for (let i = 0; i < rol.campos.length; i += n) {
      const campos = rol.campos.slice(i, i + n);
      const ids = new Set(campos.map(c => c.id));
      const partidos = rol.partidos.filter(p => ids.has(p.campo_id));
      const horarios = rol.horarios.filter(h => partidos.some(p => p.horario === h));
      porCampos.push({ ...rol, campos, partidos, horarios: horarios.length ? horarios : rol.horarios });
    }
  }

  // 2) por horarios, si el riel no cabe
  const bloques = [];
  porCampos.forEach(b => {
    const tope = maxFilas(b.campos.length);
    if (!tope) throw new Error('Demasiadas canchas para una imagen: usa PDF.');
    if (b.horarios.length <= tope) { bloques.push(b); return; }
    for (let i = 0; i < b.horarios.length; i += tope) {
      const horarios = b.horarios.slice(i, i + tope);
      const enBloque = new Set(horarios);
      bloques.push({ ...b, horarios, partidos: b.partidos.filter(p => enBloque.has(p.horario)) });
    }
  });

  return bloques.map((b, i) => ({ ...b, parte: i + 1, partes: bloques.length }));
}

export function etiquetaGenerado(iso) {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const f = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(d).replace('.', '');
  const h = new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return `Generado ${f} · ${h}`;
}

// Acota cada selector del CSS a `.rolj` para que no choque con la app.
export function acotarCss(css, pre = '.rolj') {
  return css.replace(/(^|\})(\s*)([^{}]+?)\s*\{/g, (m, cierre, ws, sel) =>
    `${cierre}${ws}${sel.split(',').map(s => `${pre} ${s.trim()}`).join(',')}{`);
}

/**
 * @param {Rol} rol
 * @param {Record<string,Club>} clubes  indexado por id
 * @param {{tema?:'oscuro'|'claro', fondoUrl?:string, velo?:number}} opciones
 * @returns {{ w:number, h:number, css:string, html:string }}
 */
export function renderRolFragmento(rol, clubes, opciones = {}) {
  const tema = TEMAS[opciones.tema ?? 'oscuro'];
  const claro = (opciones.tema ?? 'oscuro') === 'claro';
  const L = elegirLienzo(rol.campos.length, filasDelRol(rol));
  if (L.pdf) throw new Error(L.motivo);
  if (L.partir) throw new Error('Esta jornada requiere partirse; usa partirRol y renderiza cada bloque.');

  const nc = rol.campos.length;
  const catPorId = Object.fromEntries(rol.categorias.map(c => [c.id, c]));
  const idx = new Map(rol.partidos.map(p => [`${p.campo_id}|${p.horario}`, p]));

  const velo = opciones.velo ?? (claro ? [0.58, 0.74] : [0.56, 0.68]);
  const capaFondo = opciones.fondoUrl
    ? `background-image:linear-gradient(rgba(${claro ? '255,255,255' : '13,13,13'},${velo[0]}),` +
      `rgba(${claro ? '255,255,255' : '13,13,13'},${velo[1]})),url(${opciones.fondoUrl});` +
      `background-size:cover;background-position:center;`
    : '';

  const equipo = (club, esLocal) => {
    const c = club || { nombre: 'Por definir' };
    const n = nombreCorto(c);
    const px = tamanoNombre(n);
    return `<div class="tm${esLocal ? ' h' : ''}">${escudo(c, 26)}` +
           `<span class="nm" style="font-size:${px}px">${esc(n)}</span></div>`;
  };

  const tarjeta = (p) => {
    const cat = catPorId[p.categoria_id] || { etiqueta_corta: '', [tema.colorCat]: '#8A8A8A' };
    const color = cat[tema.colorCat] || '#8A8A8A';
    return `<div class="cell"><div class="tarjeta" style="border-left-color:${color}">
      ${equipo(clubes[p.local_id], true)}${equipo(clubes[p.visitante_id], false)}
      <div class="id"><b style="color:${color}">${esc(cat.etiqueta_corta)}</b> · ${esc(p.id)}</div>
    </div></div>`;
  };
  const vacia = `<div class="cell"><div class="free">SIN PARTIDO</div></div>`;

  const filas = rol.horarios.length;
  // Con pocas filas el lienzo del spec deja un vacio enorme (una tarjeta estirada en
  // 1350 px). El alto se ajusta al contenido, con tope de 230 px por fila, y nunca pasa
  // del lienzo elegido. El ancho no cambia: es el limite duro del spec.
  const H_FILA_MAX = 230;
  const hEfectiva = Math.min(L.h, METRICAS.CABECERA + METRICAS.PIE + METRICAS.ZONAS_ANOTACION + filas * H_FILA_MAX);
  const columnas = rol.campos.map(campo => {
    const celdas = rol.horarios.map(h => {
      const p = idx.get(`${campo.id}|${h}`);
      return p ? tarjeta(p) : vacia;
    }).join('');
    return `<div class="fld"><div class="ez"><b>${esc(campo.nombre)}</b>${campo.superficie ? `<span>${esc(campo.superficie)}</span>` : ''}</div>
      <div class="turf">${celdas}</div><div class="ez-b"></div></div>`;
  }).join('');

  const riel = rol.horarios.map(h =>
    `<div class="s"><div class="hr">${esc(h)}</div></div>`).join('');

  const leyenda = rol.categorias.map(c =>
    `<span class="i"><i class="sw" style="background:${c[tema.colorCat]}"></i>${esc(c.nombre)}</span>`).join('');

  const descansos = (rol.descansos || []).map(d =>
    `<b>${esc(catPorId[d.categoria_id]?.nombre ?? '')}:</b> ${d.club_ids.map(i => esc(clubes[i]?.nombre ?? i)).join(', ')}`).join(' · ');

  // Intl inserta una coma tras el día de la semana en es-MX; la quitamos.
  const fecha = rol.fecha
    ? new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
        .format(new Date(rol.fecha)).replace(',', '')
    : 'Fecha por definir';
  const fechaCap = fecha.charAt(0).toUpperCase() + fecha.slice(1);
  const parte = rol.partes > 1 ? ` · Parte ${rol.parte} de ${rol.partes}` : '';
  const torneoLinea = rol.torneo && rol.torneo !== rol.liga ? `${esc(rol.torneo)} · ` : '';
  // Pie (el dueno, 2026-09-07): sin "el primer equipo es el local" ni la fecha de
  // generacion (esa viaja en el nombre del archivo). Solo lo que exista: tolerancia,
  // coordinador, descansos; y el sello "Generado con MYKLUBB".
  const pie = [
    rol.tolerancia_min ? `Tolerancia de ${rol.tolerancia_min} minutos` : null,
    rol.coordinador?.nombre ? `Coordina ${esc(rol.coordinador.nombre)}${rol.coordinador.telefono ? ` · ${esc(rol.coordinador.telefono)}` : ''}` : null,
  ].filter(Boolean).join(' · ');
  // Solo LOGO_H (literal propio) entra sin escapar; todo lo que viene del rol pasa por esc().
  const sello = rol.version ? `<span>CALENDARIO v${esc(rol.version)}</span>` : `<span>Generado con</span>${LOGO_H}`;

  const css = `
.c{width:${L.w}px;height:${hEfectiva}px;position:relative;overflow:hidden;background:${tema.fondo};${capaFondo}color:${tema.tinta};display:flex;flex-direction:column;font-family:'Barlow',system-ui,sans-serif;margin:0;}
.c *{box-sizing:border-box;}
.head{position:relative;padding:24px 32px 20px;overflow:hidden;flex:0 0 ${METRICAS.CABECERA}px;display:flex;align-items:center;gap:26px;}
.head .diag{position:absolute;background:${tema.diagonal};width:1400px;height:96px;transform:rotate(-45deg);top:96px;left:${L.w - 380}px;}
/* La cabecera lleva la identidad de la liga o del torneo (el dueno, 2026-09-07); MYKLUBB va en el pie. */
.orglogo{position:relative;z-index:2;flex:0 0 auto;display:flex;align-items:center;}
.orglogo img{height:112px;width:auto;max-width:220px;object-fit:contain;display:block;}
.titulos{position:relative;z-index:2;flex:1;min-width:0;}
.eyebrow{font-family:'Barlow Condensed';font-weight:600;font-size:26px;letter-spacing:.26em;color:${claro ? '#454545' : '#B3F000'};line-height:1.2;}
h1{font-family:'Barlow Condensed';font-weight:800;font-size:60px;line-height:1.12;margin:2px 0 0;white-space:nowrap;}
.sub{font-size:23px;line-height:1.35;color:${tema.tenue};margin-top:6px;}
.pitch{flex:1;display:grid;grid-template-columns:94px repeat(${nc},1fr);gap:8px;padding:6px 32px 0;min-height:0;}
.rail,.fld{display:flex;flex-direction:column;min-height:0;min-width:0;}
.rail-top{height:80px;flex:0 0 80px;}
.rail .s{flex:1;min-height:0;display:flex;flex-direction:column;justify-content:center;padding-right:6px;}
.rail .s .hr{font-family:'Barlow Condensed';font-weight:800;font-size:36px;line-height:1.08;}
.ez{height:80px;flex:0 0 80px;padding:12px 10px 0;overflow:hidden;border-top:5px solid ${tema.filete};
background:repeating-linear-gradient(45deg,rgba(255,255,255,.05) 0 5px,transparent 5px 13px),${tema.zona};}
.ez b{display:block;font-family:'Barlow Condensed';font-weight:800;font-size:32px;line-height:1.18;text-transform:uppercase;color:${tema.zonaTexto};}
.ez span{display:block;font-size:17px;color:#9A9A9A;margin-top:2px;line-height:1.25;white-space:nowrap;overflow:hidden;}
.turf{flex:1;min-height:0;display:flex;flex-direction:column;position:relative;overflow:hidden;
background:repeating-linear-gradient(to bottom,${tema.vetas} 0 2px,transparent 2px 40px),${tema.cancha};}
.turf::before,.turf::after{content:'';position:absolute;top:0;bottom:0;width:2px;
background:repeating-linear-gradient(to bottom,${tema.hash} 0 9px,transparent 9px 40px);}
.turf::before{left:16px;} .turf::after{right:16px;}
.cell{flex:1;min-height:0;padding:6px;display:flex;}
.tarjeta{flex:1;min-width:0;background:${tema.tarjeta};border-left:5px solid #8A8A8A;box-shadow:${tema.sombra};
padding:10px 7px 10px 9px;display:flex;flex-direction:column;justify-content:center;overflow:hidden;}
.hora{font-family:'Barlow Condensed';font-weight:800;font-size:22px;line-height:1.2;color:${tema.tinta};margin-bottom:4px;letter-spacing:.04em;}
.tm{display:flex;align-items:center;gap:8px;min-width:0;} .tm+.tm{margin-top:5px;}
.lg{flex:0 0 26px;display:block;}
.nm{font-family:'Barlow Condensed';font-weight:600;font-size:23px;line-height:1.34;white-space:nowrap;}
.tm.h .nm{color:${tema.tinta};} .tm:not(.h) .nm{color:${tema.visitante};}
.id{margin-top:9px;font-family:'Barlow Condensed';font-weight:600;font-size:16px;letter-spacing:.06em;color:${tema.apagado};line-height:1.3;}
.free{flex:1;min-width:0;border:1px dashed ${tema.linea};display:flex;align-items:center;justify-content:center;
font-family:'Barlow Condensed';font-weight:600;font-size:21px;letter-spacing:.12em;color:${tema.apagado};}
.ez-b{height:26px;flex:0 0 26px;background:repeating-linear-gradient(45deg,rgba(255,255,255,.05) 0 5px,transparent 5px 13px),${tema.zona};}
.foot{flex:0 0 auto;padding:16px 32px 26px;}
.legend{display:flex;flex-wrap:wrap;gap:6px 20px;padding-bottom:12px;}
.legend .i{display:flex;align-items:center;gap:7px;font-size:19px;color:${claro ? '#333' : '#CFCFCF'};}
.legend .sw{width:14px;height:14px;flex:0 0 14px;}
.row{border-top:1px solid ${tema.linea};padding-top:12px;display:flex;justify-content:space-between;align-items:flex-end;gap:22px;font-size:18px;color:${tema.tenue};line-height:1.45;}
.stamp{display:flex;align-items:center;gap:10px;font-family:'Barlow Condensed';font-weight:600;letter-spacing:.12em;font-size:18px;text-transform:uppercase;white-space:nowrap;color:${tema.apagado};}
.stamp .mklogo{height:22px;width:auto;display:block;opacity:.85;}
${claro ? '.stamp .mklogo path[fill="white"]{fill:#0D0D0D;}' : ''}
`;

  const html = `<div class="rolj"><div class="c">
<div class="head"><div class="diag"></div>${rol.logo_url ? `<div class="orglogo"><img src="${esc(rol.logo_url)}" alt=""></div>` : ''}
<div class="titulos">
<div class="eyebrow">${[rol.jornada ? `JORNADA ${String(rol.jornada).padStart(2, '0')}` : '', esc(String(rol.liga || '').toUpperCase())].filter(Boolean).join(' · ')}${parte}</div>
<h1>${esc(fechaCap)}</h1>
<div class="sub">${torneoLinea}${esc(rol.sede || '')} · ${rol.partidos.length} ${rol.partidos.length === 1 ? 'partido' : 'partidos'} · ${rol.categorias.length} ${rol.categorias.length === 1 ? 'categoría' : 'categorías'}</div></div></div>
<div class="pitch"><div class="rail"><div class="rail-top"></div>${riel}<div style="height:26px;flex:0 0 26px"></div></div>${columnas}</div>
<div class="foot"><div class="legend">${leyenda}</div>
<div class="row"><div>${[pie, descansos ? `Descansan: ${descansos}` : ''].filter(Boolean).join('<br>')}</div>
<div class="stamp">${sello}</div></div></div>
</div></div>`;

  return { w: L.w, h: hEfectiva, filas, css: acotarCss(css), html };
}

/** Documento HTML completo (para render fuera de la app o pruebas). */
export function renderRol(rol, clubes, opciones = {}) {
  const f = renderRolFragmento(rol, clubes, opciones);
  return `<!DOCTYPE html><html lang="es-MX"><head><meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;800&family=Barlow:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>body{margin:0;}${f.css}</style></head><body>${f.html}</body></html>`;
}
